import { Routes } from '@/lib/constants';
import { getBrandLogMetadata } from '@/lib/brand/loggingMetadata';
import {
  resolveBrandContextForHost,
  resolveRequestHostFromHeaders,
} from '@/lib/brand/approvedHosts';
import type { IncomingMessage, ServerResponse } from 'http';
import type { NextApiRequest } from 'next';
import type { GetServerSidePropsContext } from 'next';

export const AUTH_FORCE_LOGOUT_HEADER = 'x-auth-force-logout';
export const AUTH_FORCE_LOGOUT_REASON_HEADER = 'x-auth-force-logout-reason';
export const AUTH_FORCE_LOGOUT_SOURCE_HEADER = 'x-auth-force-logout-source';

export type SessionIntegrityReason =
  | 'token_sub_user_not_found'
  | 'token_user_claim_id_mismatch_sub'
  | 'token_user_claim_email_mismatch_db_user'
  | 'token_email_mismatch_db_user'
  | 'token_email_resolves_different_user_than_sub'
  | 'auth_version_mismatch'
  | 'host_access_revoked'
  | 'session_state_user_id_mismatch'
  | 'token_present_session_hydration_failed';

export type SessionIntegritySource =
  | 'getServerSession'
  | 'session-state'
  | 'requirePageSession'
  | 'api';

export type SessionIntegrityIssue = {
  reason: SessionIntegrityReason;
  mismatchReasons: SessionIntegrityReason[];
  source: SessionIntegritySource;
  detectedAt: string;
  summary: string;
  context?: Record<string, unknown>;
};

type AuthAwareRequest = (
  | NextApiRequest
  | GetServerSidePropsContext['req']
  | IncomingMessage
) & {
  __sessionIntegrityIssue?: SessionIntegrityIssue;
};

type HeaderCapableResponse = Pick<ServerResponse, 'getHeader' | 'setHeader'> & {
  headersSent?: boolean;
};

const AUTH_COOKIE_EXPIRY = 'Thu, 01 Jan 1970 00:00:00 GMT';

const SESSION_TOKEN_COOKIE_BASES = [
  { name: '__Secure-next-auth.session-token', secure: true, httpOnly: true },
  { name: 'next-auth.session-token', secure: false, httpOnly: true },
] as const;

const SESSION_TOKEN_COOKIE_NAME_PATTERN =
  /^(?:__Secure-)?next-auth\.session-token(?:\.\d+)?$/;

const SESSION_TOKEN_CHUNK_SUFFIXES = [
  '',
  ...Array.from({ length: 21 }, (_, index) => `.${index}`),
];

const AUTH_COOKIE_VARIANTS = [
  ...SESSION_TOKEN_COOKIE_BASES.flatMap((base) =>
    SESSION_TOKEN_CHUNK_SUFFIXES.map((suffix) => ({
      name: `${base.name}${suffix}`,
      secure: base.secure,
      httpOnly: base.httpOnly,
    }))
  ),
  { name: '__Secure-next-auth.callback-url', secure: true, httpOnly: false },
  { name: 'next-auth.callback-url', secure: false, httpOnly: false },
  { name: '__Host-next-auth.csrf-token', secure: true, httpOnly: false },
  { name: '__Secure-next-auth.csrf-token', secure: true, httpOnly: false },
  { name: 'next-auth.csrf-token', secure: false, httpOnly: false },
  {
    name: '__Secure-next-auth.pkce.code_verifier',
    secure: true,
    httpOnly: true,
  },
  { name: 'next-auth.pkce.code_verifier', secure: false, httpOnly: true },
  { name: '__Secure-next-auth.state', secure: true, httpOnly: false },
  { name: 'next-auth.state', secure: false, httpOnly: false },
  { name: '__Secure-next-auth.nonce', secure: true, httpOnly: false },
  { name: 'next-auth.nonce', secure: false, httpOnly: false },
] as const;

const normalizeReasonForHeader = (value: string): string =>
  value.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);

const expiredCookie = (
  name: string,
  secure: boolean,
  httpOnly: boolean
): string =>
  [
    `${name}=`,
    'Path=/',
    `Expires=${AUTH_COOKIE_EXPIRY}`,
    'Max-Age=0',
    'SameSite=Lax',
    secure ? 'Secure' : null,
    httpOnly ? 'HttpOnly' : null,
  ]
    .filter(Boolean)
    .join('; ');

const getObservedSessionTokenCookieVariants = (
  req?: AuthAwareRequest
): Array<{ name: string; secure: boolean; httpOnly: boolean }> => {
  if (!req) return [];

  return getCookieNames(req.headers?.cookie)
    .filter((name) => SESSION_TOKEN_COOKIE_NAME_PATTERN.test(name))
    .map((name) => ({
      name,
      secure: name.startsWith('__Secure-'),
      httpOnly: true,
    }));
};

const getForceLogoutCookieHeadersForRequest = (
  req?: AuthAwareRequest
): string[] => {
  const variantsByName = new Map<
    string,
    { name: string; secure: boolean; httpOnly: boolean }
  >();

  for (const variant of [
    ...AUTH_COOKIE_VARIANTS,
    ...getObservedSessionTokenCookieVariants(req),
  ]) {
    variantsByName.set(variant.name, variant);
  }

  return Array.from(variantsByName.values()).map((variant) =>
    expiredCookie(variant.name, variant.secure, variant.httpOnly)
  );
};

const getExistingCookieHeaders = (
  res: HeaderCapableResponse
): string[] | null => {
  const existing = res.getHeader('Set-Cookie');
  if (!existing) return null;
  if (Array.isArray(existing)) {
    return existing.map((value) => String(value));
  }
  return [String(existing)];
};

const mergeCookieHeaders = (
  existing: string[] | null,
  next: string[]
): string[] => {
  if (!existing || existing.length === 0) return next;
  return [...existing, ...next];
};

export const markSessionIntegrityIssue = (
  req: AuthAwareRequest,
  issue: SessionIntegrityIssue
) => {
  req.__sessionIntegrityIssue = issue;
};

export const getSessionIntegrityIssue = (
  req: AuthAwareRequest
): SessionIntegrityIssue | null => req.__sessionIntegrityIssue ?? null;

export const clearSessionIntegrityIssue = (req: AuthAwareRequest) => {
  delete req.__sessionIntegrityIssue;
};

export const applyForcedLogoutHeaders = (
  res: HeaderCapableResponse | undefined,
  reason: SessionIntegrityReason,
  source: SessionIntegritySource = 'api',
  req?: AuthAwareRequest
) => {
  if (!res || res.headersSent) return;
  if (String(res.getHeader(AUTH_FORCE_LOGOUT_HEADER) ?? '') === '1') return;

  const existingCookies = getExistingCookieHeaders(res);
  const logoutCookies = getForceLogoutCookieHeadersForRequest(req);
  const mergedCookies = mergeCookieHeaders(existingCookies, logoutCookies);

  res.setHeader('Set-Cookie', mergedCookies);
  res.setHeader(AUTH_FORCE_LOGOUT_HEADER, '1');
  res.setHeader(
    AUTH_FORCE_LOGOUT_REASON_HEADER,
    normalizeReasonForHeader(reason)
  );
  res.setHeader(AUTH_FORCE_LOGOUT_SOURCE_HEADER, source);
};

export const applyForcedLogoutIfNeeded = (
  req: AuthAwareRequest,
  res: HeaderCapableResponse | undefined
): SessionIntegrityIssue | null => {
  const issue = getSessionIntegrityIssue(req);
  if (!issue) return null;
  applyForcedLogoutHeaders(res, issue.reason, issue.source, req);
  clearSessionIntegrityIssue(req);
  return issue;
};

export const buildForceLogoutSignInDestination = ({
  callbackUrl,
  reason,
}: {
  callbackUrl?: string;
  reason?: SessionIntegrityReason | string;
} = {}): string => {
  const params = new URLSearchParams({ forceLogout: '1' });
  if (callbackUrl) {
    params.set('callbackUrl', callbackUrl);
  }
  if (reason) {
    params.set('reason', reason);
  }
  return `${Routes.SITE.LOGIN}?${params.toString()}`;
};

const normalizeHeaderValue = (value: string | string[] | undefined): string => {
  if (Array.isArray(value)) return value.join(', ');
  return value ?? '';
};

export const getCookieNames = (
  cookieHeader: string | string[] | undefined
): string[] => {
  const raw = normalizeHeaderValue(cookieHeader);
  if (!raw) return [];

  const names = raw
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => part.split('=')[0]?.trim())
    .filter((name): name is string => Boolean(name));

  return Array.from(new Set(names)).sort();
};

export const extractAuthRequestContext = (
  req: AuthAwareRequest
): Record<string, unknown> => {
  const brand = resolveBrandContextForHost(
    resolveRequestHostFromHeaders(req.headers ?? {})
  );

  return {
    ...getBrandLogMetadata(brand),
    method: (req as NextApiRequest).method,
    url: req.url ?? null,
    host: normalizeHeaderValue(req.headers?.host),
    origin: normalizeHeaderValue(req.headers?.origin as string | undefined),
    referer: normalizeHeaderValue(
      (req.headers?.referer ?? req.headers?.referrer) as string | undefined
    ),
    userAgent: normalizeHeaderValue(req.headers?.['user-agent']),
    xForwardedFor: normalizeHeaderValue(req.headers?.['x-forwarded-for']),
    cookieNames: getCookieNames(req.headers?.cookie),
  };
};
